import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { PropertyCategories } from 'rich-text-lib/constants/properties';
import { getPathsMap } from 'rich-text-lib/utils/buildProperties';
import { TokenImage } from 'rich-text-lib/utils/TokenImage';
import { parseHtml, replaceNodes, addPostProcessTransform, addPreProcessTransform } from '../utils/transformer';
import { getHublVariable } from '../utils/hubl';
import { trackInteraction } from '../utils/usageTracking';
import { PERSONALIZATION_TOKEN_CASING } from '../constants/usageTracking';
import { HUBL_PATH, HUBL_PERSONALIZATION_PATTERN, FILTER_PATTERN_FRAGMENT, hsDataTokenConfig } from '../constants/hsdatatokens';
import { HS_PLUGINS } from '../constants/hsSettings';
import { __HS_DATA_TOKEN } from '../constants/plugins';
import { RSS_CATEGORY } from '../constants/hsrsstokens';
import { BreezeSingleStarIcon } from 'foundations-assets/icons/components/BreezeSingleStarIcon';
import { CartIcon } from 'foundations-assets/icons/components/CartIcon';
import { CompaniesIcon } from 'foundations-assets/icons/components/CompaniesIcon';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { DealsIcon } from 'foundations-assets/icons/components/DealsIcon';
import { HubDBIcon } from 'foundations-assets/icons/components/HubDBIcon';
import { InvoiceIcon } from 'foundations-assets/icons/components/InvoiceIcon';
import { SalesQuoteIcon } from 'foundations-assets/icons/components/SalesQuoteIcon';
import { TicketIcon } from 'foundations-assets/icons/components/TicketIcon';
export const AI_DYNAMIC_TOKEN_CATEGORY = 'ai_dynamic_token';
export const createTokenString = ({
  path = '',
  defaultValue,
  filter,
  prefix
}) => {
  // Use prefix if provided, otherwise use the original path
  const tokenPath = prefix ? `${prefix}.${path.split('.').slice(1).join('.')}` : path;
  if (defaultValue === null || defaultValue === undefined) {
    return `{{ ${tokenPath}${filter ? `|${filter}` : ''} }}`;
  }
  return `{{ personalization_token('${tokenPath}', '${defaultValue}')${filter ? `|${filter}` : ''} }}`;
};
const getHasFilterSupport = dataTokenConfig => {
  const propertyFiltersCallback = dataTokenConfig[hsDataTokenConfig.PROPERTY_FILTERS_CALLBACK];
  return !!propertyFiltersCallback;
};
const getPrefixesFromConfig = dataTokenConfig => {
  const customObjectPrefixes = dataTokenConfig[hsDataTokenConfig.CUSTOM_OBJECT_PREFIXES];
  if (typeof customObjectPrefixes !== 'object' || !customObjectPrefixes) return [];
  return Object.values(customObjectPrefixes).map(prefix => prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // escape regex special characters
  );
};
export const getTokenPattern = (dataTokenConfig, useDefault = false) => {
  // Return appropriate fallback pattern based on useDefault flag
  if (!dataTokenConfig) {
    return useDefault ? HUBL_PERSONALIZATION_PATTERN : HUBL_PATH;
  }
  const hasFilterSupport = getHasFilterSupport(dataTokenConfig);
  const prefixes = getPrefixesFromConfig(dataTokenConfig);

  // Return appropriate fallback pattern if no custom configuration
  if (!hasFilterSupport && prefixes.length === 0) {
    return useDefault ? HUBL_PERSONALIZATION_PATTERN : HUBL_PATH;
  }

  // Build pattern that includes prefixes in the capture group
  const prefixPattern = prefixes.length > 0 ? `|${prefixes.join('|')}` : '';
  const filterPattern = hasFilterSupport ? FILTER_PATTERN_FRAGMENT : '';

  // this regex is used by the pre/post process transforms for HubL matching and replacement logic
  if (useDefault) {
    // Pattern for personalization_token('path', 'default') format
    return new RegExp(`{{-?\\s*personalization_token\\(\\s*["']([a-zA-Z0-9_()\\-[\\]]+(?:[.][a-zA-Z0-9_()\\-[\\]]+)*${prefixPattern})["']\\s*,\\s*["'](.*?)["']{1}\\s*\\)${filterPattern}\\s*-?}}`, 'g');
  } else {
    // Pattern for simple {{ path }} format
    return new RegExp(`{{-?\\s*([a-zA-Z0-9_()\\-[\\]]+(?:[.][a-zA-Z0-9_()\\-[\\]]+)*${prefixPattern})\\s*${filterPattern}\\s*-?}}`, 'g');
  }
};
export const getPropertyOnlyTokenPattern = dataTokenConfig => {
  if (!dataTokenConfig) {
    return new RegExp('{\\{\\s*(subscription_name)?\\s*\\}\\}', 'g');
  }
  const prefixes = getPrefixesFromConfig(dataTokenConfig);
  if (prefixes.length === 0) {
    return new RegExp('{\\{\\s*(subscription_name)?\\s*\\}\\}', 'g');
  }
  return new RegExp(`{\\{\\s*(subscription_name${prefixes.length !== 0 ? `|${prefixes.join('|')}` : ''})?\\s*\\}\\}`, 'g');
};
export const getTokenPatternWithDefault = dataTokenConfig => {
  return getTokenPattern(dataTokenConfig, true);
};
export const getDefaultValueType = defaultValue => {
  if (defaultValue === '') {
    return 'empty';
  } else if (defaultValue === null) {
    return 'global';
  } else {
    return 'local';
  }
};
export function getHsDataTokenSettings(editor) {
  const pluginSettings = editor.settings ? editor.settings[HS_PLUGINS] : null;
  if (!pluginSettings || !pluginSettings[__HS_DATA_TOKEN]) {
    return null;
  }
  return pluginSettings[__HS_DATA_TOKEN];
}
const getTokenIconFromCategory = (category = '') => {
  switch (category.toLowerCase()) {
    case PropertyCategories.CONTACT:
      return ContactsIcon;
    case PropertyCategories.COMPANY:
      return CompaniesIcon;
    case PropertyCategories.DEAL:
      return DealsIcon;
    case PropertyCategories.QUOTE:
    case PropertyCategories.LINE_ITEM:
      return SalesQuoteIcon;
    case PropertyCategories.TICKET:
      return TicketIcon;
    case PropertyCategories.INVOICE:
      return InvoiceIcon;
    case PropertyCategories.CART:
      return CartIcon;
    case AI_DYNAMIC_TOKEN_CATEGORY:
      return BreezeSingleStarIcon;
    default:
      return HubDBIcon;
  }
};

/**
 *
 * @param {String} hublVar token hubl string
 * @returns {Object} token propertites
 */

export const splitHublVar = hublVar => {
  const parts = hublVar.split('.');
  if (!parts.length) {
    return null;
  }
  if (parts.length === 1) {
    return {
      property: parts[0]
    };
  }
  return {
    category: parts[0],
    property: parts[1]
  };
};

/**
 *
 * @param {String} str maybe token hubl string
 * @returns {(Object|null)} token propertites
 */
export const getTokenProperties = str => {
  const hublVar = getHublVariable(str);
  if (!hublVar) {
    return null;
  }
  return splitHublVar(hublVar);
};

/**
 *
 * @param {string} dataTokenPath
 * @param {DataTokenSettingsConfig} dataTokenConfig
 * @returns {string} Translated property label
 */
export const getTokenLabel = (dataTokenPath, dataTokenConfig) => {
  const pathsMap = getPathsMap(dataTokenConfig) || {};
  const pathData = pathsMap[dataTokenPath];
  return pathData ? pathData.label : '';
};
export const isEditableToken = (editor, node) => {
  return editor.dom.is(node, 'img[data-mce-hubspot-object=true][data-mce-placeholder=true][data-mce-property-category!=post]');
};
export function tokenIsOnlyChild(editor, node) {
  if (isEditableToken(editor, node)) return true;
  const childNodes = [...node.childNodes];
  if (childNodes.length === 1) {
    return tokenIsOnlyChild(editor, childNodes[0]);
  }
  return false;
}

// We want to keep the token styling consistent across all tokens
// These colors are hardcoded to prevent the token from changing colors when the theme changes
const TOKEN_COLORS = {
  ICON_COLOR: '#33475b',
  TEXT_COLOR: '#33475b',
  AI_DYNAMIC_ICON_COLOR: '#D20688',
  BACKGROUND_DEFAULT: '#ffffff',
  BACKGROUND_DISABLED: '#fdedee',
  BORDER_DEFAULT: '#cbd6e2',
  BORDER_DISABLED: '#f2545b'
};
export const renderTokenBase = (label, category, additionalImgProps = {}, maxTokenWidth, hasDataSensitivity) => {
  const IconComponent = getTokenIconFromCategory(category);
  const iconColor = category === AI_DYNAMIC_TOKEN_CATEGORY ? TOKEN_COLORS.AI_DYNAMIC_ICON_COLOR : TOKEN_COLORS.ICON_COLOR;
  const backgroundColor = hasDataSensitivity ? TOKEN_COLORS.BACKGROUND_DISABLED : TOKEN_COLORS.BACKGROUND_DEFAULT;
  const borderColor = hasDataSensitivity ? TOKEN_COLORS.BORDER_DISABLED : TOKEN_COLORS.BORDER_DEFAULT;
  const tokenImageElement = /*#__PURE__*/createElement(TokenImage, {
    IconComponent,
    text: label,
    iconColor,
    textColor: TOKEN_COLORS.TEXT_COLOR,
    backgroundColor,
    borderColor,
    maxWidth: maxTokenWidth,
    imgProps: Object.assign({
      'data-mce-placeholder': 'true',
      'data-mce-resize': 'false',
      className: 'mce-token'
    }, additionalImgProps)
  });
  const htmlString = renderToStaticMarkup(tokenImageElement);
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const image = doc.querySelector('img');
  if (!image) {
    return null;
  }
  return image;
};

// Check for an improperly cased (lowercased) `category` compared
// to the appropriate case-sensitive FQN, then track this in amplitude.
// Part of https://git.hubteam.com/HubSpot/Critsit/issues/2576 postmortem.
const checkForImproperlyCasedFQNs = (editor, supportedCrmObjectFQNs, category) => {
  const properlyCasedFQN = supportedCrmObjectFQNs.find(fqn => category === fqn.toLowerCase() && category !== fqn);
  if (properlyCasedFQN) {
    trackInteraction(editor, 'tinymcePersonalize', PERSONALIZATION_TOKEN_CASING, {
      category,
      fqn: properlyCasedFQN
    });
  }
  return !!properlyCasedFQN;
};
const replaceHubLWithToken = (editor, supportedCrmObjectFQNs, textContent, tokenPatternArray, renderFunction) => {
  let newTextContent = textContent;
  tokenPatternArray.forEach(({
    TOKEN_PATTERN,
    USES_DEFAULT
  }) => {
    if (USES_DEFAULT) {
      newTextContent = newTextContent.replace(TOKEN_PATTERN, (fullMatch, path, defaultValue, filter) => {
        if (path.startsWith(RSS_CATEGORY)) return fullMatch;
        const category = path.split('.')[0];
        const isImproperlyCasedFQN = checkForImproperlyCasedFQNs(editor, supportedCrmObjectFQNs, category);
        if (isImproperlyCasedFQN) return fullMatch;
        return renderFunction(Object.assign({
          path,
          category,
          defaultValue
        }, typeof filter === 'string' && {
          filter
        })) || fullMatch;
      });
    } else {
      newTextContent = newTextContent.replace(TOKEN_PATTERN, (fullMatch, path, filter) => {
        if (path.startsWith(RSS_CATEGORY)) return fullMatch;
        const category = path.split('.')[0];
        const isImproperlyCasedFQN = checkForImproperlyCasedFQNs(editor, supportedCrmObjectFQNs, category);
        if (isImproperlyCasedFQN) return fullMatch;
        return renderFunction(Object.assign({
          path,
          category
        }, typeof filter === 'string' && {
          filter
        })) || fullMatch;
      });
    }
  });
  return newTextContent;
};
export const addTokenTransforms = (editor, supportedCrmObjectFQNs, tokenPatternArray, encode, renderPlaceHolder, replacePlaceholderWithHubl) => {
  const replaceHubLWithPlaceholder = tokenNode => {
    const newTextContent = replaceHubLWithToken(editor, supportedCrmObjectFQNs, encode(tokenNode.textContent), tokenPatternArray, renderPlaceHolder);
    const doc = parseHtml(`<div>${newTextContent}</div>`);
    const firstChild = doc.body.firstChild;
    return firstChild && firstChild.childNodes;
  };
  addPreProcessTransform(editor, dom => {
    const textNodeIterator = dom.createNodeIterator(dom.body, NodeFilter.SHOW_TEXT, node => {
      tokenPatternArray.forEach(({
        TOKEN_PATTERN
      }) => {
        TOKEN_PATTERN.lastIndex = 0;
      });
      const nodeTextContent = node.textContent || '';
      if (tokenPatternArray.some(({
        TOKEN_PATTERN
      }) => {
        return TOKEN_PATTERN.test(nodeTextContent);
      })) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_REJECT;
    });
    const textNodes = [];
    let currentNode = textNodeIterator.nextNode();
    while (currentNode) {
      textNodes.push(currentNode);
      currentNode = textNodeIterator.nextNode();
    }
    replaceNodes(textNodes, replaceHubLWithPlaceholder);
  });
  addPostProcessTransform(editor, dom => {
    const EXCLUDED_TOKEN_SELECTORS = `:not([data-mce-rss-link]):not(.${AI_DYNAMIC_TOKEN_CATEGORY}):not([data-mce-property-category="${AI_DYNAMIC_TOKEN_CATEGORY}"])`;
    const tokenPlaceholderNodes = dom.querySelectorAll(`[data-mce-token-path]${EXCLUDED_TOKEN_SELECTORS},[data-mce-property-name]${EXCLUDED_TOKEN_SELECTORS}`);
    replaceNodes(tokenPlaceholderNodes, replacePlaceholderWithHubl);
  });
};